import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { 
    Target, Heart, Users, ArrowRight, 
    CheckCircle, Lightbulb, TrendingUp
} from 'lucide-react';
import SEO, { SEO_CONFIG } from '../components/SEO';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";

export default function AboutPage({ onRunAudit, onNavigate }) {
    const seo = SEO_CONFIG.about;
    const painPoints = [
        "Why they're not ranking on Google",
        "Why visitors aren't converting into customers",
        "What technical issues exist on their site",
        "How to prioritize website improvements"
    ];

    const values = [
        {
            icon: Target,
            title: "Accessibility",
            description: "Professional website audits should be available to everyone, not just big companies with big budgets."
        },
        {
            icon: Lightbulb,
            title: "Clarity",
            description: "Technical jargon creates confusion. We translate complex issues into plain English action steps."
        },
        {
            icon: TrendingUp,
            title: "Actionability",
            description: "A report is only valuable if you can act on it. Every recommendation comes with clear next steps."
        }
    ];

    return (
        <div className="min-h-screen bg-black text-white">
            <SEO title={seo.title} description={seo.description} keywords={seo.keywords} />
            
            <Navbar activePage="about" onNavigate={onNavigate} onRunAudit={onRunAudit} />

            {/* Hero Section */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6">
                        Built for Small Businesses That Want{' '}
                        <span className="bg-gradient-to-r from-cyan-400 to-magenta bg-clip-text text-transparent">
                            Big Results
                        </span>
                    </h1>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                        SiteGradePro was created to help entrepreneurs, startups, and local businesses 
                        understand what's holding their website back — without needing a developer.
                    </p>
                </div>
            </section>

            {/* Problem Section */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto">
                    <Card className="bg-white/5 border-white/10">
                        <CardContent className="p-8 md:p-12">
                            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">
                                The Problem We Solve
                            </h2>
                            <p className="text-gray-400 text-lg text-center mb-8">
                                Many website owners don't know:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {painPoints.map((point, i) => (
                                    <div key={i} className="flex items-start gap-3 bg-black/30 rounded-lg p-4">
                                        <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center shrink-0 mt-0.5">
                                            <span className="text-red-400 text-sm">?</span>
                                        </div>
                                        <span className="text-gray-300">{point}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="text-center mt-10">
                                <p className="text-2xl font-bold text-magenta">
                                    SiteGradePro makes it simple.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Values Section */}
            <section className="py-16 px-4 bg-gradient-to-b from-transparent to-white/5">
                <div className="max-w-5xl mx-auto">
                    <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                        What We Believe
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {values.map((value, i) => {
                            const Icon = value.icon;
                            return (
                                <Card key={i} className="bg-white/5 border-white/10 hover:border-magenta/30 transition-colors">
                                    <CardContent className="p-6 text-center">
                                        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-magenta/20 to-purple-500/20 border border-magenta/20 flex items-center justify-center mx-auto mb-6">
                                            <Icon className="h-8 w-8 text-magenta" />
                                        </div>
                                        <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                                        <p className="text-gray-400">{value.description}</p>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Mission Statement */}
            <section className="py-20 px-4">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500/20 to-magenta/20 border border-cyan-500/20 flex items-center justify-center mx-auto mb-8">
                        <Heart className="h-10 w-10 text-magenta" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Mission</h2>
                    <p className="text-xl text-gray-300 leading-relaxed">
                        To make professional website audits{' '}
                        <span className="text-cyan-400 font-semibold">accessible</span>,{' '}
                        <span className="text-magenta font-semibold">affordable</span>, and{' '}
                        <span className="text-green-400 font-semibold">actionable</span>{' '}
                        for businesses of all sizes.
                    </p>
                </div>
            </section>

            {/* Team/Trust Section */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto">
                    <Card className="bg-gradient-to-br from-magenta/10 to-cyan-500/10 border-magenta/30">
                        <CardContent className="p-8 md:p-12 text-center">
                            <Users className="h-12 w-12 text-magenta mx-auto mb-6" />
                            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                Trusted by Growing Businesses
                            </h2>
                            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
                                SiteGradePro has helped thousands of entrepreneurs and small business owners 
                                understand their website's potential and take action to improve it.
                            </p>
                            <div className="flex flex-wrap justify-center gap-8 text-center">
                                <div>
                                    <div className="text-4xl font-bold text-white mb-1">10K+</div>
                                    <div className="text-gray-500 text-sm">Sites Analyzed</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold text-white mb-1">95%</div>
                                    <div className="text-gray-500 text-sm">Satisfaction Rate</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold text-white mb-1">50+</div>
                                    <div className="text-gray-500 text-sm">Countries Served</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        Ready to See What's Possible?
                    </h2>
                    <p className="text-gray-400 mb-8 text-lg">
                        Run your free website audit and discover how to improve your online presence.
                    </p>
                    <Button 
                        onClick={onRunAudit}
                        size="lg"
                        className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-10 py-6 text-lg"
                    >
                        Run Your Free Website Audit
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <Footer onNavigate={onNavigate} />
        </div>
    );
}
